import { MainApi } from 'api/MainApi'
import { Button } from 'components/Button/Button'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import Title from 'components/Title/Title'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from 'react-modal'

import style from './RenameRobotMapModal.module.css'

interface IDialogProps {
  isOpen: boolean
  mapName: string
  mapId: number
  onSave: () => void
  onClose: () => void
}

const RenameRobotMapModal = ({ isOpen, mapName, mapId, onClose, onSave }: React.PropsWithChildren<IDialogProps>) => {
  const { t } = useTranslation()

  const [tempMapName, setTempMapName] = useState(mapName)
  const [hasNameError, setNameHasError] = useState(false)
  const [actionInProgress, setActionInProgress] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setTempMapName(mapName)
      setNameHasError(false)
    }
  }, [isOpen, mapName])

  const onMapNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      setNameHasError(true)
    } else {
      setNameHasError(false)
      setTempMapName(e.target.value)
    }
  }

  const onCancelClicked = () => {
    if (!actionInProgress) {
      onClose()
    }
  }

  const onSaveClicked = async () => {
    if (tempMapName === '') {
      setNameHasError(true)
      return
    }

    setActionInProgress(true)
    try {
      if (tempMapName !== mapName) {
        await MainApi.renameRobotMap(mapId, tempMapName)
        onSave()
      }
      onClose()
    } catch (error) {
      console.error('Error renaming robot map:', error)
    } finally {
      setActionInProgress(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onCancelClicked}
      className={style.root}
      style={{
        overlay: {
          zIndex: 100,
          backgroundColor: 'rgba(27, 43, 72, 0.6)',
        },
      }}
      ariaHideApp={false}
    >
      <div className={style.modal}>
        <div className={style.head}>
          <Title text={'Rename the map'} className={style.title} />
        </div>
        <div className={style.content}>
          <div className={style.row}>
            <TextFieldDescribed
              className={style.textfield}
              description={''}
              error={t('view_zones.name_error')}
              placeholder={t('view_zones.name_placeholder')}
              hasError={hasNameError}
              value={tempMapName}
              onChange={onMapNameChanged}
            />
          </div>
        </div>
        <div className={style.footer}>
          <Button.Outlined className={style.btn} onClick={onCancelClicked} disabled={actionInProgress}>
            {t('mission_settings.label_cancel')}
          </Button.Outlined>
          <Button.Standart
            disabled={hasNameError || actionInProgress}
            loading={actionInProgress}
            className={style.btn}
            onClick={onSaveClicked}
          >
            {t('mission_settings.label_save')}
          </Button.Standart>
        </div>
      </div>
    </Modal>
  )
}

export default RenameRobotMapModal
